import React, { useMemo } from "react";
import { motion } from "framer-motion";
import Particles, { ParticlesProvider } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { ArrowRight, Sparkles } from "lucide-react";

const LETTERS = ["D", "A", "T", "A", "V", "I", "Q", "U"];

// Pre-generated random origin coordinates for each letter (so the assembly looks orbital/space-like)
const ORIGINS = [
  { x: -420, y: -260, r: -45, s: 0.4 },
  { x: 380, y: -320, r: 70, s: 0.5 },
  { x: -260, y: 340, r: -30, s: 0.6 },
  { x: 320, y: 280, r: 50, s: 0.4 },
  { x: -380, y: -120, r: -65, s: 0.5 },
  { x: 280, y: -240, r: 35, s: 0.6 },
  { x: -120, y: 360, r: -50, s: 0.4 },
  { x: 420, y: 140, r: 60, s: 0.5 },
];

export default function Hero() {
  const initParticles = async (engine) => {
    await loadSlim(engine);
  };

  const particlesOptions = useMemo(
    () => ({
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 45,
      pauseOnOutsideViewport: true,
      pauseOnBlur: true,
      particles: {
        number: { value: 35, density: { enable: true, area: 1000 } },
        color: { value: ["#ffffff", "#2563EB", "#8ab4ff"] },
        shape: { type: "circle" },
        opacity: {
          value: { min: 0.1, max: 0.6 },
        },
        size: { value: { min: 0.5, max: 1.6 } },
        move: {
          enable: true,
          speed: 0.2,
          direction: "none",
          random: true,
          straight: false,
          outModes: { default: "out" },
        },
        links: { enable: false },
      },
      detectRetina: true,
    }),
    []
  );

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center grain"
    >
      {/* Particle layer */}
      <div className="absolute inset-0 z-0">
        <ParticlesProvider init={initParticles}>
          <Particles id="zenoviq-particles" options={particlesOptions} className="w-full h-full" />
        </ParticlesProvider>
      </div>

      {/* Radial blue glow */}
      <div className="absolute inset-0 z-0 glow-blue pointer-events-none" />

      {/* Orbit rings */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none">
        <div className="relative w-[480px] h-[480px] md:w-[760px] md:h-[760px]">
          <div className="orbit-ring opacity-40" />
          <div className="orbit-ring opacity-30" style={{ inset: "10%", animationDuration: "60s", animationDirection: "reverse" }} />
          <div className="orbit-ring opacity-20" style={{ inset: "22%", animationDuration: "80s" }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.7 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs font-mono uppercase tracking-[0.2em] text-zinc-300"
          data-testid="hero-eyebrow"
        >
          <Sparkles className="w-3 h-3 text-blue-400" />
          See Within Data
        </motion.div>

        {/* Letter assembly */}
        <div
          className="mt-10 mb-8 flex flex-wrap items-center justify-center select-none max-w-full"
          data-testid="hero-dataviqu-letters"
          aria-label="DATAVIQU"
        >
          {LETTERS.map((char, i) => (
            <motion.span
              key={`${char}-${i}`}
              initial={{
                opacity: 0,
                x: ORIGINS[i].x,
                y: ORIGINS[i].y,
                rotate: ORIGINS[i].r,
                scale: ORIGINS[i].s,
                filter: "blur(8px)",
              }}
              animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, filter: "blur(0px)" }}
              transition={{
                delay: 0.2 + i * 0.18,
                duration: 1.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="font-display font-medium text-white inline-block leading-none"
              style={{
                fontSize: "clamp(32px, 9vw, 160px)",
                letterSpacing: "-0.04em",
                textShadow: "0 0 40px rgba(37,99,235,0.25)",
              }}
            >
              {char}
            </motion.span>
          ))}
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.7 }}
          className="font-display text-xl md:text-3xl font-light text-white/90 max-w-3xl mx-auto"
          data-testid="hero-tagline"
        >
          See Within Data. <span className="text-blue-400">Clarity by Design.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1, duration: 0.7 }}
          className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-supporting-text"
        >
          Dataviqu builds smart data solutions, financial intelligence, and stunning visual designs for forward-thinking businesses.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.3, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => {
              const el = document.getElementById("contact");
              if (!el) return;
              window.__lenis ? window.__lenis.scrollTo(el, { offset: -64 }) : el.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-100 transition-all"
          >
            Start a project
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            onClick={() => {
              const el = document.getElementById("services");
              if (!el) return;
              window.__lenis ? window.__lenis.scrollTo(el, { offset: -64 }) : el.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/5 transition-all"
          >
            Explore services
          </button>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 flex flex-col items-center gap-2"
      >
        {/* <span>scroll</span> */}
        <span className="block w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
}
